import { get, set, del } from 'idb-keyval';
import type { DishExplanation } from '../../shared/menu';

interface StoredExplanation {
  explanation: DishExplanation;
  dishHash: string;
  savedAt: number;
}

// Per-(menuId, dishId, targetLanguage) cache. dishHash gates staleness when
// the user re-parses a menu and dish IDs/allergens change underneath them.
// The server cache uses the same key shape; this just mirrors it locally
// for instant re-display + offline access after first online fetch.
//
// `-v2` prefix bumped when the explain panel reframed from conversation
// prompts to declarative "What is this dish?" content. Old cache entries
// under the `explain:` prefix are orphaned (harmless — IDB just leaves
// them) and the first fetch produces fresh v2 content.
function key(menuId: string, dishId: string, targetLanguage: string): string {
  return `explain-v2:${menuId}:${dishId}:${targetLanguage}`;
}

export async function loadExplanationLocal(
  menuId: string,
  dishId: string,
  targetLanguage: string,
): Promise<StoredExplanation | null> {
  const raw = await get<StoredExplanation>(key(menuId, dishId, targetLanguage));
  return raw ?? null;
}

export async function saveExplanationLocal(
  menuId: string,
  dishId: string,
  targetLanguage: string,
  explanation: DishExplanation,
  dishHash: string,
): Promise<void> {
  await set(key(menuId, dishId, targetLanguage), {
    explanation,
    dishHash,
    savedAt: Date.now(),
  } satisfies StoredExplanation);
}

export async function removeExplanationLocal(
  menuId: string,
  dishId: string,
  targetLanguage: string,
): Promise<void> {
  await del(key(menuId, dishId, targetLanguage));
}
